import type {
  DataResponse,
  PaginatedResponse,
  Task,
  TaskDetail,
  TaskListItem,
  TaskTimelineEvent,
  EmployeeTaskSummary,
  CreateTaskInput,
  UpdateTaskInput,
  ListTasksQuery,
  ListTaskReviewQuery,
  TaskReviewItem,
  AddTaskAssigneesInput,
  ReorderTaskAssigneesInput,
  SetOccurrenceStatusInput,
  TaskTypeSetting,
  TaskConfigSettings,
  UpdateTaskConfigInput,
  RunTaskSchedulerInput,
  TaskDailyRollResult,
  TaskReminderSweepResult,
  TaskType,
} from '@pms/shared';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';

export const tasksService = {
  async list(query: Partial<ListTasksQuery> = {}): Promise<PaginatedResponse<TaskListItem>> {
    const res = await api.get<PaginatedResponse<TaskListItem>>('/tasks', { params: query });
    return res.data;
  },
  async get(id: string): Promise<TaskDetail> {
    const res = await api.get<DataResponse<TaskDetail>>(`/tasks/${id}`);
    return res.data.data;
  },
  async create(input: CreateTaskInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/tasks', input);
    return res.data.data;
  },
  async update(id: string, input: UpdateTaskInput): Promise<void> {
    await api.patch(`/tasks/${id}`, input);
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/tasks/${id}`);
  },

  // ── Voice note ──────────────────────────────────────────────────────────────
  /**
   * Uploads the recording captured in the browser. The Content-Type header is left
   * to axios so it sets the multipart boundary, and the timeout is raised well past
   * the shared 15s default — a multi-MB note on a weak mobile connection would
   * otherwise abort mid-upload.
   */
  async uploadVoiceNote(taskId: string, blob: Blob, durationMs: number): Promise<void> {
    const form = new FormData();
    form.append('file', blob, 'voice-note');
    await api.post(`/tasks/${taskId}/voice-note`, form, {
      params: { duration_ms: Math.round(durationMs) },
      timeout: 120_000,
    });
  },

  async deleteVoiceNote(taskId: string): Promise<void> {
    await api.delete(`/tasks/${taskId}/voice-note`);
  },
  async stop(id: string): Promise<void> {
    await api.post(`/tasks/${id}/stop`);
  },
  async reactivate(id: string): Promise<void> {
    await api.post(`/tasks/${id}/reactivate`);
  },
  async addAssignees(id: string, input: AddTaskAssigneesInput): Promise<{ added: number }> {
    const res = await api.post<DataResponse<{ added: number }>>(`/tasks/${id}/assignees`, input);
    return res.data.data;
  },
  async removeAssignee(id: string, userId: string): Promise<void> {
    await api.delete(`/tasks/${id}/assignees/${userId}`);
  },
  /** Important tasks — rewrite the fallback chain; array position becomes the sequence. */
  async reorderAssignees(id: string, input: ReorderTaskAssigneesInput): Promise<void> {
    await api.patch(`/tasks/${id}/assignees/order`, input);
  },
  async timeline(id: string): Promise<TaskTimelineEvent[]> {
    const res = await api.get<DataResponse<TaskTimelineEvent[]>>(`/tasks/${id}/timeline`);
    return res.data.data;
  },

  // Employee-centric views
  async byEmployee(params: { department_id?: string; q?: string } = {}): Promise<EmployeeTaskSummary[]> {
    const res = await api.get<DataResponse<EmployeeTaskSummary[]>>('/tasks/by-employee', { params });
    return res.data.data;
  },
  async myTasks(): Promise<TaskListItem[]> {
    const res = await api.get<DataResponse<TaskListItem[]>>('/tasks/my');
    return res.data.data;
  },
  async assignedByMe(): Promise<TaskListItem[]> {
    const res = await api.get<DataResponse<TaskListItem[]>>('/tasks/assigned-by-me');
    return res.data.data;
  },
  async assignedByMeEmployees(): Promise<EmployeeTaskSummary[]> {
    const res = await api.get<DataResponse<EmployeeTaskSummary[]>>('/tasks/assigned-by-me/employees');
    return res.data.data;
  },
  async tasksForEmployee(userId: string): Promise<TaskListItem[]> {
    const res = await api.get<DataResponse<TaskListItem[]>>(`/tasks/employees/${userId}`);
    return res.data.data;
  },
  async stoppedCount(): Promise<number> {
    const res = await api.get<DataResponse<{ count: number }>>('/tasks/stopped-count');
    return res.data.data.count;
  },

  // ── Review queue ────────────────────────────────────────────────────────────
  // Scoped server-side to tasks the caller created; there is no filter to pass for that.
  async reviewQueue(
    query: Partial<ListTaskReviewQuery> = {},
  ): Promise<PaginatedResponse<TaskReviewItem>> {
    const res = await api.get<PaginatedResponse<TaskReviewItem>>('/tasks/review', { params: query });
    return res.data;
  },
  async reviewCount(): Promise<number> {
    const res = await api.get<DataResponse<{ count: number }>>('/tasks/review-count');
    return res.data.data.count;
  },

  // Occurrence status — guarded transitions, records to the timeline.
  /**
   * Returns the timeline event this wrote, so a reviewer can attach a voice note to it.
   * `timeline_id` is null when the call changed nothing (the status was already that).
   */
  async setStatus(
    occAssigneeId: string, input: SetOccurrenceStatusInput,
  ): Promise<{ timeline_id: string | null }> {
    const res = await api.post<DataResponse<{ timeline_id: string | null }>>(
      `/tasks/occurrence-assignees/${occAssigneeId}/status`, input,
    );
    return res.data.data;
  },

  /** Attaches a recording to one timeline event. Mirrors uploadVoiceNote's contract. */
  async uploadTimelineVoiceNote(timelineId: string, blob: Blob, durationMs: number): Promise<void> {
    const form = new FormData();
    form.append('file', blob, 'voice-note');
    await api.post(`/tasks/timeline/${timelineId}/voice-note`, form, {
      params: { duration_ms: Math.round(durationMs) },
      timeout: 120_000,
    });
  },

  /**
   * Sets a status and, if a recording came with it, attaches that to the timeline event
   * the change produced.
   *
   * The audio failure is RETURNED, not thrown: the status change has already committed
   * and must not be reported as failed — the same split CreateTaskModal uses for a task's
   * voice note. Callers surface `voiceError` as a warning.
   *
   * `timeline_id` is null when the status was already what was asked for; there is no
   * event to attach to, so the recording is simply dropped.
   */
  async setStatusWithVoice(
    occAssigneeId: string,
    input: SetOccurrenceStatusInput,
    voice: { blob: Blob; durationMs: number } | null,
  ): Promise<{ voiceError: string | null }> {
    const { timeline_id: timelineId } = await this.setStatus(occAssigneeId, input);
    if (!voice || !timelineId) return { voiceError: null };
    try {
      await this.uploadTimelineVoiceNote(timelineId, voice.blob, voice.durationMs);
      return { voiceError: null };
    } catch (e) {
      return { voiceError: getErrorMessage(e) };
    }
  },

  // Config
  async getTaskTypeSettings(): Promise<TaskTypeSetting[]> {
    const res = await api.get<DataResponse<TaskTypeSetting[]>>('/tasks/config/task-types');
    return res.data.data;
  },
  async setTaskTypeEnabled(taskType: TaskType, isEnabled: boolean): Promise<void> {
    await api.patch(`/tasks/config/task-types/${taskType}`, { is_enabled: isEnabled });
  },
  async getConfigSettings(): Promise<TaskConfigSettings> {
    const res = await api.get<DataResponse<TaskConfigSettings>>('/tasks/config/settings');
    return res.data.data;
  },
  async updateConfigSettings(input: UpdateTaskConfigInput): Promise<void> {
    await api.patch('/tasks/config/settings', input);
  },

  // Scheduler run-now. The crons run these on a tick; these trigger a pass immediately.
  async runDailyRoll(input: RunTaskSchedulerInput = {}): Promise<TaskDailyRollResult> {
    const res = await api.post<DataResponse<TaskDailyRollResult>>('/tasks/admin/run-daily-roll', input);
    return res.data.data;
  },
  async runReminders(): Promise<TaskReminderSweepResult> {
    const res = await api.post<DataResponse<TaskReminderSweepResult>>('/tasks/admin/run-reminders', {});
    return res.data.data;
  },
};

export type { Task };
